// src/pages/ProductsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './ProductsPage.css';

const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStrain, setSelectedStrain] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const categories = ['all', ...new Set(products.map(p => p.category))];
  const strains = ['all', ...new Set(products.map(p => p.strain))];
  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name-asc', label: 'Name: A to Z' },
    { value: 'name-desc', label: 'Name: Z to A' },
    { value: 'newest', label: 'Newest First' }
  ];

  // Filter products with loading simulation
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedCategory, selectedStrain, searchTerm, sortBy]);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesStrain = selectedStrain === 'all' || product.strain === selectedStrain;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesStrain && matchesSearch;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'newest':
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      default:
        return a.isFeatured ? -1 : 1;
    }
  });

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      'weekly-special': '🏷️',
      'Flower': '🌿',
      'Exotic-Flower': '🌸',
      'Edibles': '🍬',
      'Concentrates': '⚗️',
      'cart': '🔋',
      'disposable-cart': '💨',
      'Pre-rolls': '🚬',
      'Shake': '🍃'
    };
    return icons[category] || '📦';
  };

  // Get active filters count
  const activeFiltersCount = [
    selectedCategory !== 'all',
    selectedStrain !== 'all',
    searchTerm !== ''
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedStrain('all');
    setSearchTerm('');
    setSortBy('featured');
    setShowMobileFilters(false);
  };

  return (
    <div className="products-page">
      {/* Hero Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">All Products</h1>
          <p className="page-subtitle">Browse our complete selection of premium cannabis products</p>
        </div>
        <div className="page-header-actions">
          <button 
            className="mobile-filter-btn"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            aria-label={showMobileFilters ? "Hide filters" : "Show filters"}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {activeFiltersCount > 0 && (
              <span className="filter-count">{activeFiltersCount}</span>
            )}
          </button>
        </div>
      </div>

      <div className="products-container">
        {/* Filters Sidebar - Desktop */}
        <aside className={`filters-sidebar ${showMobileFilters ? 'mobile-visible' : ''}`}>
          <div className="filters-header">
            <h3 className="filters-title">Filters</h3>
            <button 
              className="close-filters-btn"
              onClick={() => setShowMobileFilters(false)}
              aria-label="Close filters"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="filter-group">
            <label className="filter-label">
              <svg viewBox="0 0 24 24" fill="none" className="filter-icon">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Search Products
            </label>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <svg viewBox="0 0 24 24" fill="none" className="filter-icon">
                <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" 
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Category
            </label>
            <div className="category-grid">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`category-chip ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <span className="category-icon">{cat !== 'all' && getCategoryIcon(cat)}</span>
                  <span className="category-name">
                    {cat === 'all' ? 'All Categories' : cat.replace('-', ' ')}
                  </span>
                  {cat !== 'all' && (
                    <span className="category-count">
                      {products.filter(p => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Strain Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <svg viewBox="0 0 24 24" fill="none" className="filter-icon">
                <path d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Strain Type
            </label>
            <div className="strain-grid">
              {strains.map(strain => (
                <button
                  key={strain}
                  className={`strain-chip ${selectedStrain === strain ? 'active' : ''}`}
                  onClick={() => setSelectedStrain(strain)}
                >
                  {strain === 'all' ? 'All Strains' : strain}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Filters */}
          <div className="filter-actions">
            <button
              onClick={resetFilters}
              className="reset-filters-btn"
              disabled={activeFiltersCount === 0}
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Reset All Filters
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="products-main">
          {/* Filters Bar */}
          <div className="filters-bar">
            <div className="filters-info">
              <p className="results-count">
                Showing <span className="results-highlight">{sortedProducts.length}</span> of{' '}
                <span className="results-total">{products.length}</span> products
              </p>
              {activeFiltersCount > 0 && (
                <div className="active-filters">
                  {selectedCategory !== 'all' && (
                    <span className="active-filter">
                      {selectedCategory.replace('-', ' ')}
                      <button onClick={() => setSelectedCategory('all')}>×</button>
                    </span>
                  )}
                  {selectedStrain !== 'all' && (
                    <span className="active-filter">
                      {selectedStrain}
                      <button onClick={() => setSelectedStrain('all')}>×</button>
                    </span>
                  )}
                  {searchTerm && (
                    <span className="active-filter">
                      "{searchTerm}"
                      <button onClick={() => setSearchTerm('')}>×</button>
                    </span>
                  )}
                </div>
              )}
            </div>
            
            <div className="sort-container">
              <label className="sort-label">
                <svg viewBox="0 0 24 24" fill="none" className="sort-icon">
                  <path d="M3 4h18M3 8h12M3 12h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Products Grid */}
          {isLoading ? (
            <div className="loading-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="product-skeleton">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                    <div className="skeleton-line shorter"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : sortedProducts.length > 0 ? (
            <div className="products-grid">
              {sortedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h2 className="empty-state-title">No products found</h2>
              <p className="empty-state-text">Try adjusting your search or filters to find what you're looking for</p>
              <button
                onClick={resetFilters}
                className="empty-state-btn"
              >
                Reset All Filters
              </button>
            </div>
          )}

          {/* No Results Message */}
          {sortedProducts.length === 0 && !isLoading && (
            <div className="suggestions">
              <h3 className="suggestions-title">Suggestions:</h3>
              <ul className="suggestions-list">
                <li>Check your spelling</li>
                <li>Try more general keywords</li>
                <li>Browse by category instead</li>
                <li>Check out our featured products</li>
              </ul>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProductsPage;